







































import { computed, defineComponent, ref, watch } from '@nuxtjs/composition-api';
import useMyLocation from '@shared/composables/useMyLocation';
import useMyStore from '@shared/composables/useMyStore';
import useNearbyStores from '@shared/composables/useNearbyStores';
import useResponsive from '@shared/composables/useResponsive';
import useUiState from '@shared/composables/useUiState';
import { NearbyStoreParams } from '@shared/types/locations';
import { StoreDetails } from '@shared/types/stores';
import { UCIcon } from '@shared/ui';
import { Logger } from '@vue-storefront/core';

import NearestStoreSearch from '~/components/Stores/NearestStoreSearch.vue';
import StoreLocatorHeader from '~/components/Stores/StoreLocatorHeader.vue';
import StoresList from '~/components/Stores/StoresList.vue';
import StoresPicker from '~/components/Stores/StoresPicker.vue';

export default defineComponent({
  name: 'StoreLocatorBanner',
  components: { StoresList, StoreLocatorHeader, NearestStoreSearch, StoresPicker, UCIcon },
  // eslint-disable-next-line max-lines-per-function
  setup() {
    const { isStoresSidebarOpen, toggleStoresSidebar } = useUiState();
    const stores = ref<StoreDetails[] | null>(null);
    const { myStore } = useMyStore();
    const { isMobile } = useResponsive();
    const { saveLocation } = useMyLocation();
    const showSuccessBanner = ref(false);
    const successTimeout = ref<number | undefined>();
    const nearbyStoresLoading = ref(false);
    const showStoresPicker = ref(false);
    const { getNearbyStores } = useNearbyStores(`stores-banner`);

    const reset = () => {
      stores.value = null;
      showSuccessBanner.value = false;
      nearbyStoresLoading.value = false;
      toggleStoresSidebar(false);
      setShowStoresPicker(false);
      window.clearTimeout(successTimeout.value);
    };

    const searchNearbyStores = async ({ lat, lng, placeId }: NearbyStoreParams) => {
      setShowStoresPicker(false);
      nearbyStoresLoading.value = true;
      stores.value = null;
      try {
        stores.value = await getNearbyStores({ lat, lng, placeId });
        if (lat && lng) saveLocation({ lat, lng });
      } catch (err) {
        Logger.error('Failed to fetch nearby stores', err);
      } finally {
        nearbyStoresLoading.value = false;
      }
    };

    const storeLabel = computed(() =>
      myStore.value ? `${myStore.value.shortName} (${myStore.value.state})` : '',
    );

    watch(
      myStore,
      (store, prevStore) => {
        if ((!prevStore && !store) || prevStore?.name === store?.name || !isMobile.value) return;
        window.scrollTo(0, 0);
        stores.value = null;
        toggleStoresSidebar(true);
        showSuccessBanner.value = true;
        successTimeout.value = window.setTimeout(() => {
          reset();
        }, 1500);
      },
      { immediate: false },
    );

    watch(isStoresSidebarOpen, () => {
      if (!isStoresSidebarOpen.value) {
        reset();
      }
    });

    const setShowStoresPicker = (val: boolean) => {
      showStoresPicker.value = val;
    };

    return {
      myStore,
      isStoresSidebarOpen,
      stores,
      showSuccessBanner,
      nearbyStoresLoading,
      storeLabel,
      toggleStoresSidebar,
      searchNearbyStores,
      setShowStoresPicker,
      showStoresPicker,
    };
  },
});
